import { Injectable } from '@angular/core';
import type { ApiParams } from '@shared/http/api-params';
import type { CarerBookingAllocation } from '@shared/modules/carer-booking-allocations/carer-booking-allocation';
import type { HolidayPayEstimate, LeaveAccrual } from '@shared/modules/carer-leaves/carer-leaves';
import type { CarerPatchBody } from '@shared/modules/carers/carer-patch.body';
import type { Document } from '@shared/modules/documents/document';
import type { ScheduleDatesContext } from '@shared/modules/schedule/schedule-dates-context';
import type { Team } from '@shared/modules/teams/team';
import type { User } from '@shared/modules/users/user';
import type { CreateVaccinationPayload } from '@shared/modules/vaccinations/create-vaccination-payload';
import type { Vaccination } from '@shared/modules/vaccinations/vaccination';
import type { DateTime } from 'luxon';
import type { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/operators';
import { Fetcher } from '../../http/fetcher';
import type { Carer } from './carer';

export interface LeaveYearTotals {
    total_accrued: number;
    total_taken: number;
}

interface CreateCarerUserPayload {
    password: null;
    password_confirmation: null;
    email: null;
    restoration_options: {
        restore: boolean;
        update: boolean;
    };
}

@Injectable({
    providedIn: 'root',
})
export class CarerFetcher extends Fetcher<Carer> {
    readonly modelName: string = 'Carer';

    get resourceName(): string {
        return 'carers';
    }

    patch<T = Carer>(key: string, body: CarerPatchBody, params: ApiParams = {}): Observable<T> {
        return super.patch<T, CarerPatchBody>(key, body, params);
    }

    getBookingAllocation(
        carerId: string,
        allocationId: string,
        params: ApiParams = {}
    ): Observable<CarerBookingAllocation> {
        return this._get<CarerBookingAllocation>(
            `${this._getUrl(carerId)}/booking-allocations/${allocationId}`,
            params
        ).pipe(map(response => response.data));
    }

    setTeams(carerId: string, payload: { teams: Team[]; default_team_id: string }, params: ApiParams = {}) {
        return this._post(
            `${this._getUrl(carerId)}/teams`,
            {
                default_team_id: payload.default_team_id,
                teams: {
                    data: payload.teams.map(({ id }) => ({ id })),
                },
            },
            params
        );
    }

    createUser(carerId: string, body: CreateCarerUserPayload) {
        return this._post<User>(this._getUrl(carerId, 'user'), body).pipe(map(response => response.data));
    }

    vaccinations(carerId: string, params: ApiParams = {}) {
        return this._get<Vaccination[]>(this._getUrl(carerId, 'vaccinations'), params);
    }

    createVaccination(carerId: string, body: CreateVaccinationPayload, params: ApiParams = {}) {
        return this._post<Vaccination>(this._getUrl(carerId, 'vaccinations'), body, params);
    }

    deleteVaccination(carerId: string, vaccinationId: string, params: ApiParams = {}) {
        return this._delete(this._getUrl(carerId, 'vaccinations', vaccinationId), params);
    }

    recentVisitLocations(carerId: string) {
        return this._get<{ lat: number; lng: number }[]>(this._getUrl(carerId, 'recent-visit-locations'));
    }

    getHoursWorking(carerId: string, from: DateTime, to: DateTime) {
        const url = this._getUrl(carerId, 'bookings', 'hours');
        const start = from.toSQLDate();
        const finish = to.toSQLDate();
        return this._get<{ hours: number; seconds: number }>(url, { start, finish });
    }

    notifyAgenda(body: CarerAgendaExportPayload) {
        return this._post<{ status: string }>('/export/carers/agenda/notify', body, {
            start_date_gte: body.start_date_gte,
            start_date_lte: body.start_date_lte,
        });
    }

    printAgenda(body: CarerAgendaExportPayload, email: string) {
        return this._post<{ status: string }>('/export/carers/agenda', { ...body, email });
    }

    tickleAppInstallations(carerId: string) {
        return this._post<{ sent: true }>(this._getUrl(carerId, 'app-installations', 'tickle'), {});
    }

    getScheduleDates(id: string, dates: DateTime[]) {
        return this._post<ScheduleDatesContext>(this._getUrl(id, 'schedule/dates'), {
            dates: dates.map(date => date.toISODate()),
        }).pipe(map(response => response.data));
    }

    updateDefaultContract(carerId, contractId) {
        return this._put(this._getUrl(carerId, 'default-contract'), {
            carer_contract_id: contractId,
        });
    }

    getStarredDocuments(id: string, count: number): Observable<Document[]> {
        return this._get<Document[]>(`${this._getUrl(id)}/documents`, { is_starred: true, count, include: 'type' })
            .pipe(
                map(response => response.data),
            );
    }

    getHolidayPayEstimate(carerId: string): Observable<HolidayPayEstimate> {
        return this._get<HolidayPayEstimate>(this._getUrl(carerId, 'leave', 'part-time-holiday-estimate'))
            .pipe(map(response => response.data));
    }

    getLeaveAccrual(carerId: string): Observable<LeaveAccrual> {
        return this._get<LeaveAccrual>(this._getUrl(carerId, 'leave', 'accrual'))
            .pipe(map(response => response.data));
    }

    /**
     * The carer's accrued and taken hours for the leave year containing the given date.
     */
    getLeaveYearTotals(carerId: string, date: string): Observable<LeaveYearTotals> {
        return this._get<LeaveYearTotals>(this._getUrl(carerId, 'leave', 'total-accrual'), { date })
            .pipe(map(response => response.data));
    }

    workAssignmentStats(carerId: string, date: DateTime, week: boolean = false) {
        return this._get<WorkPatternAssignmentStats[]>(this._getUrl(carerId, 'work-pattern-assignments/stats'), {
            date: date.toSQLDate(),
            week,
        });
    }
}

export interface WorkPatternAssignmentStats {
    id: string;
    name: string;
    flattened_duration: number;
    bookings_duration: number;
    expected_duration: number;
    pattern_id: number;
    min_week: number;
    max_week: number;
    shifts_duration: number;
}

export interface CarerAgendaExportPayload {
    carers: { id: string }[];
    start_date_gte: string;
    start_date_lte: string;
    envelope?: string;
    hide_client_address?: boolean;
    include_cancelled_bookings?: boolean;
    name_format?: string;
    show_client_phone_number?: boolean;
}
